import { Box, Flex, Group, Text } from '@mantine/core';
import { useTranslation } from 'locales';
import { isNonNullish, sumBy } from 'remeda';
import type { PaidByReminderWorkflow } from 'shared/hooks/use-load-paid-by-reminder';
import { useSafeLocalizedCompactCurrencyFormatter } from 'shared/utils/currency';

import { PaidByReminderBar } from './PaidByReminderBar';
import { PaidByReminderWorkflowTooltip } from './PaidByReminderWorkflowTooltip';
import {
  type PaidByReminderSegment,
  segmentKey,
  toPaidByReminderSegments,
} from './paid-by-reminder-segments';
import { formatPercentage } from './percentage';

const FIGURE = { fontVariantNumeric: 'tabular-nums' } as const;

const collectedAmount = ({ steps }: PaidByReminderWorkflow) =>
  sumBy(
    steps.filter(({ stepType }) => isNonNullish(stepType)),
    bucket => bucket.amount,
  );

const paidShare = (segments: ReadonlyArray<PaidByReminderSegment>) =>
  sumBy(
    segments.filter(({ kind }) => kind === 'step'),
    ({ share }) => share,
  );

type PaidByReminderWorkflowsProps = {
  workflows: ReadonlyArray<PaidByReminderWorkflow>;
};

export const PaidByReminderWorkflows = ({ workflows }: PaidByReminderWorkflowsProps) => {
  const { t } = useTranslation();
  const formatAmount = useSafeLocalizedCompactCurrencyFormatter();

  return (
    <Flex component="ul" direction="column" gap="md" style={{ padding: 0, listStyleType: 'none' }}>
      {workflows.map(workflow => {
        const segments = toPaidByReminderSegments(workflow, t);

        return (
          <Box key={workflow.id} component="li">
            <Group justify="space-between" align="baseline" wrap="nowrap" gap="xs" mb={6}>
              <Text size="sm" c="gray.8" truncate>
                {workflow.name}
              </Text>
              <Group gap="xs" wrap="nowrap">
                <Text size="sm" c="gray.6" style={FIGURE}>
                  {formatPercentage(paidShare(segments))}
                </Text>
                <Text size="sm" fw={600} c="purple.6" style={FIGURE}>
                  {formatAmount.format(collectedAmount(workflow))}
                </Text>
              </Group>
            </Group>
            <PaidByReminderBar
              segments={segments}
              tooltipFor={segment => (
                <PaidByReminderWorkflowTooltip
                  workflowName={workflow.name}
                  segments={segments}
                  highlightedKey={segmentKey(segment)}
                />
              )}
            />
          </Box>
        );
      })}
    </Flex>
  );
};
